import type { Maybe } from '@adornis/base/utilTypes.js';
import { Field } from '@adornis/baseql/decorators.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { MicrosoftData } from './MicrosoftData.js';
import { MicrosoftSyncRejection } from './MicrosoftSyncRejection.js';

@Entity()
export class MicrosoftUserData extends MicrosoftData {
  static override _class = 'MicrosoftUserData';

  @Field(type => Boolean, { default: v => v ?? false })
  shareWithMicrosoft: Maybe<boolean>;

  @Field(type => Boolean, { default: v => v ?? false })
  manuallyChangedName: Maybe<boolean>;

  @Field(type => String)
  userPrincipalName: Maybe<string>;

  @Field(type => String)
  mail: Maybe<string>;

  /**
   * The alias addresses this person should have, derived from their roles and schools.
   *
   * ! An output. Nothing types into this. !
   *
   * It used to be both: the alias dialog wrote here and the sync recomputed over the top, so a
   * hand-typed alias survived exactly until the next save of that person. Worse, the computation read
   * this field back and pushed onto it, which made it a growing residue of every previous run rather
   * than a description of the person - and produced accounts listing their own primary address as one
   * of their aliases. Hand-typed entries now live in {@link manualMails}, and this is recomputed from
   * scratch by `getDesiredUserMails`.
   */
  @Field(type => [String], { default: v => v ?? [] })
  otherMails: Maybe<string[]>;

  /**
   * Alias addresses somebody added by hand in the Microsoft tab.
   *
   * An input, and the only half of the alias list a person can edit. Everything else is derived, so
   * an alias always has a reason: either a school this person is linked to, or a deliberate entry
   * here.
   */
  @Field(type => [String], { default: v => v ?? [] })
  manualMails: Maybe<string[]>;

  /** @see MicrosoftSyncRejection */
  @Field(type => [MicrosoftSyncRejection], { default: v => v ?? [] })
  syncRejections: Maybe<MicrosoftSyncRejection[]>;

  @Field(type => String)
  givenName: Maybe<string>;

  @Field(type => String)
  surname: Maybe<string>;
}
